import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import FeaturedMembers from '../components/FeaturedMembers';
import CommunityFeed from '../components/CommunityFeed';
import NewMembersSection from '../components/NewMembersSection';
import GuestEngagementWidget from '../components/GuestEngagementWidget';
import FeaturedSuggestion from '../components/FeaturedSuggestion';
import BetaNoticeBanner from '../components/BetaNoticeBanner';
import { SidebarAd, InFeedAd } from '../components/AdSense';
import PromotionsSidebar from '../components/landing/PromotionsSidebar';
import LatestVideosWidget from '../components/LatestVideosWidget';
import FeaturedVideo from '../components/FeaturedVideo';
import PatronRecognition from '../components/PatronRecognition';
import SuccessStories from '../components/SuccessStories';
import { Card, CardContent } from '../components/ui/card';
import {
  LiveStats,
  CommunitySection,
  CTASection,
  WhyJoinSection,
  BlogPreviewSection,
  AdoptionSection,
  PartnerSection
} from '../components/landing/LandingComponents';
import {
  Dna,
  Heart,
  Building2,
  Stethoscope,
  Users,
  Globe,
  Shield,
  Microscope,
  PawPrint,
  Sparkles,
  TreeDeciduous,
  ArrowRight,
  Info,
  Newspaper
} from 'lucide-react';

// Hero features for the rotating spotlight
const heroFeatures = [
  {
    icon: Dna,
    badge: "Now Available",
    title: "DNA Testing",
    highlight: "& Breeding",
    description: "Discover breed composition, health markers, and connect with verified breeding programs.",
    linkUrl: "/breeds",
    linkText: "Explore breeds"
  },
  {
    icon: TreeDeciduous,
    badge: "Now Available",
    title: "Family Trees",
    highlight: "& Ancestry",
    description: "Visualize your pet's lineage with interactive family trees. Connect with relatives.",
    linkUrl: "/family-tree",
    linkText: "Explore ancestry"
  },
  {
    icon: Heart,
    badge: "Coming Soon",
    title: "Find Perfect",
    highlight: "Playmates",
    description: "Smart matchmaking considers temperament, location, and compatibility.",
    linkUrl: null,
    linkText: null
  },
  {
    icon: Building2,
    badge: "Now Available",
    title: "NGO & Rescue",
    highlight: "Network",
    description: "Connecting verified shelters with loving families. Every adoption starts with a connection.",
    linkUrl: "/adopt",
    linkText: "Find adoptable pets"
  },
  {
    icon: Stethoscope,
    badge: "Secure & Private",
    title: "Health Records",
    highlight: "Anywhere",
    description: "Encrypted medical records accessible from any device. Share with vets securely.",
    linkUrl: "/faq?topic=health",
    linkText: "Learn more"
  },
  {
    icon: Users,
    badge: "Join the Pack",
    title: "Topic-Based",
    highlight: "Communities",
    description: "Find your tribe! Breed-specific groups, training enthusiasts, and more.",
    linkUrl: "/communities",
    linkText: "Browse communities"
  },
  {
    icon: Globe,
    badge: "Growing Worldwide",
    title: "International",
    highlight: "Expansion",
    description: "Our community spans continents. Local meetups, global connections.",
    linkUrl: "/clubs",
    linkText: "Explore clubs"
  },
  {
    icon: Shield,
    badge: "Coming Soon",
    title: "Health",
    highlight: "Insights",
    description: "Early detection tools and wellness monitoring for your pets.",
    linkUrl: null,
    linkText: null
  },
  {
    icon: Microscope,
    badge: "Make a Difference",
    title: "Research",
    highlight: "Collaboration",
    description: "Opt-in to veterinary studies that advance animal health.",
    linkUrl: "/faq?topic=research",
    linkText: "Learn more"
  }
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentFeatureIndex((prev) => (prev + 1) % heroFeatures.length);
        setIsTransitioning(false);
      }, 400);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const currentFeature = heroFeatures[currentFeatureIndex];
  const FeatureIcon = currentFeature.icon;

  return (
    <div className="min-h-screen bg-gray-50/50" data-testid="landing-page">
      <SEO 
        title={null}
        description="All American Dog Club - A member-driven community for dog lovers. Connect with fellow dog owners, access health resources, find local parks, and join a community that puts dogs first."
        url="/"
      />
      <GuestEngagementWidget />

      {/* Compact Welcome Banner */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white" data-testid="hero-banner">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PawPrint className="w-5 h-5 text-amber-400" />
            <div>
              <h1 className="text-base font-semibold">All American Dog Club</h1>
              <p className="text-xs text-white/60">A community for pet owners and services</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/about/future')}
              className="hidden sm:flex items-center gap-1 text-xs text-white/70 hover:text-white transition"
            >
              <Sparkles className="w-3 h-3" /> Features
            </button>
            <button
              onClick={() => navigate('/about')}
              className="hidden sm:flex items-center gap-1 text-xs text-white/70 hover:text-white transition"
            >
              <Info className="w-3 h-3" /> About
            </button>
            <button
              onClick={() => navigate('/club-blog')}
              className="hidden sm:flex items-center gap-1 text-xs text-white/70 hover:text-white transition"
            >
              <Newspaper className="w-3 h-3" /> News
            </button>
          </div>
        </div>
      </div>

      {/* Beta Notice Banner */}
      <BetaNoticeBanner />

      {/* 3-Column Layout */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* LEFT PANEL - Rotating Feature Spotlight */}
          <aside className="lg:w-64 xl:w-72 shrink-0 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24 space-y-4">
              <Card className="overflow-hidden" data-testid="feature-spotlight">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-4">
                  <p className="text-[10px] uppercase tracking-widest text-amber-400/80 mb-2">What We're Building</p>
                  <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 text-[10px] text-white/80 mb-2">
                      <FeatureIcon className="w-3 h-3 text-amber-400" />
                      {currentFeature.badge}
                    </span>
                    <h3 className="text-lg font-bold text-white leading-tight">
                      {currentFeature.title}{' '}
                      <span className="text-amber-400 italic">{currentFeature.highlight}</span>
                    </h3>
                    <p className="text-xs text-white/60 mt-1.5 leading-relaxed">
                      {currentFeature.description}
                    </p>
                    {currentFeature.linkUrl && (
                      <button
                        onClick={() => navigate(currentFeature.linkUrl)}
                        className="mt-2 text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-1 transition-colors"
                      >
                        {currentFeature.linkText} <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {/* Dot navigation */}
                  <div className="flex gap-1.5 mt-3">
                    {heroFeatures.map((_, idx) => (
                      <button
                        key={idx}
                        className={`h-1 rounded-full transition-all duration-300 ${
                          idx === currentFeatureIndex
                            ? 'bg-amber-400 w-4'
                            : 'bg-white/20 w-1 hover:bg-white/40'
                        }`}
                        onClick={() => {
                          setIsTransitioning(true);
                          setTimeout(() => {
                            setCurrentFeatureIndex(idx);
                            setIsTransitioning(false);
                          }, 200);
                        }}
                        aria-label={`Feature ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </Card>

              {/* Quick links below spotlight */}
              <Card>
                <CardContent className="p-3 space-y-1.5">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Quick Links</p>
                  {[
                    { label: 'Browse Clubs', url: '/clubs', icon: Globe },
                    { label: 'Find Adoptable Pets', url: '/adopt', icon: Heart },
                    { label: 'Community Forums', url: '/communities', icon: Users },
                  ].map(link => (
                    <button
                      key={link.url}
                      onClick={() => navigate(link.url)}
                      className="flex items-center gap-2 w-full text-left text-sm text-muted-foreground hover:text-foreground py-1 transition-colors"
                      data-testid={`quick-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <link.icon className="w-3.5 h-3.5" />
                      {link.label}
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* CENTER - Main Feed */}
          <main className="flex-1 min-w-0 order-1 lg:order-2 space-y-8" data-testid="main-feed-area">
            <FeaturedVideo />
            <CommunityFeed />

            {/* In-Feed Ad after community feed */}
            <InFeedAd slot="landingInFeed" />

            {/* New Members Section */}
            <NewMembersSection />

            <FeaturedMembers limit={6} />

            <AdoptionSection />

            <SuccessStories limit={5} showHeader={true} />

            <CommunitySection />
          </main>

          {/* RIGHT PANEL - Stats & Suggestions */}
          <aside className="lg:w-64 xl:w-72 shrink-0 order-3">
            <div className="lg:sticky lg:top-24 space-y-4">
              {/* Live Stats */}
              <Card data-testid="stats-panel">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Community Stats</p>
                  <LiveStats variant="inline" />
                </CardContent>
              </Card>

              {/* Featured Suggestion */}
              <FeaturedSuggestion />

              {/* Why Join - compact */}
              <Card>
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Why Join?</p>
                  <ul className="space-y-2 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Shield className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" />
                      <span>Your data is never sold</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Dna className="w-3.5 h-3.5 mt-0.5 text-blue-600 shrink-0" />
                      <span>DNA, health records & ancestry tools</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Users className="w-3.5 h-3.5 mt-0.5 text-purple-600 shrink-0" />
                      <span>Local clubs & global community</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Sidebar Ad Placement */}
              <SidebarAd slot="landingSidebar" className="hidden lg:block" />

              {/* Latest Videos */}
              <LatestVideosWidget limit={3} className="hidden lg:block" />

              {/* Promotions */}
              <PromotionsSidebar />

              {/* Patron Recognition */}
              <PatronRecognition />
            </div>
          </aside>
        </div>
      </div>

      {/* Full-width sections below */}
      <BlogPreviewSection />
      <PartnerSection />
      <CTASection />
    </div>
  );
};

export default Landing;
